import React, { useState } from 'react';
import { Modal } from '@/components/design-system/atoms/modal';
import { SenderAndRecipient } from '@/features/admin/payouts/types';
import { useTranslation } from 'react-i18next';

interface BulkPaymentConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  groups: SenderAndRecipient[];
}

const formatEuro = (cents: number): string => {
  if (isNaN(cents) || !isFinite(cents)) return '';

  const value = Number(cents) / 100;
  return new Intl.NumberFormat('fr-BE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

export const BulkPaymentConfirmationModal: React.FC<BulkPaymentConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  groups,
}) => {
  const [pending, setPending] = useState(false);
  const { t } = useTranslation();

  const handleConfirm = async () => {
    if (pending) return;
    try {
      setPending(true);
      await onConfirm();
    } finally {
      setPending(false);
      onClose();
    }
  };
  return (
    <Modal open={isOpen} onClose={onClose}>
      <div className="flex max-h-[80vh] w-[720px] flex-col overflow-hidden rounded-lg bg-white shadow-medium">
        <div className="flex h-[102px] w-full flex-shrink-0 items-center bg-R300 px-4 py-3">
          <span className="text-h3 font-bold text-text-primaryHeavy">
            {t(
              'js.features.admin.transactions.outgoing_transactions_list.actions.payment_confirmation'
            )}
          </span>
        </div>

        <div className="flex min-h-0 w-full flex-1 flex-col overflow-hidden bg-white p-5">
          <div className="min-h-0 flex-1 overflow-hidden">
            <div className="mb-3 w-full overflow-hidden rounded-[12px] border border-[#E5E7EB] bg-[#F3F4F6] p-2">
              <div className="max-h-[420px] overflow-y-auto rounded bg-[#F3F4F6] px-3 py-4 pr-4">
                {groups.map((group, gi) => (
                  <div
                    key={gi}
                    className="min-w-0 border-t border-[#E5E7EB] pt-4 first:border-t-0 first:pt-0 last:pb-0"
                  >
                    <div className="mb-3 text-[16px] font-semibold text-text-primaryHeavy">
                      <span>From </span>
                      <span>{group.sender_detail.name}</span>
                      {group.sender_detail.reference && (
                        <span className="ml-2 text-[16px] font-semibold text-text-primaryHeavy">
                          ({group.sender_detail.reference})
                        </span>
                      )}
                    </div>

                    <div className="flex flex-col">
                      {group.payments.map((p, pi) => (
                        <div
                          key={pi}
                          className="grid min-w-0 grid-cols-[auto_auto_1fr] items-center border-b border-[#E5E7EB] last:border-b-0"
                        >
                          <div className="flex items-center gap-4 text-text-primaryHeavy">
                            <span className="text-[18px]">→</span>
                            <span className="min-w-[120px] text-[15px] font-semibold tabular-nums">
                              {formatEuro(p.amount_cents)}
                            </span>
                          </div>
                          <span className="w-[44px] text-[14px] text-[#6B7280]">To</span>
                          <span className="block min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-[14px] font-medium text-[#111827]">
                            <span className="font-semibold text-text-primaryHeavy">
                              {p.recipient.name}
                              {p.recipient.bank_account?.iban && (
                                <span className="ml-2 font-semibold text-text-primaryHeavy">
                                  ({p.recipient.bank_account.iban})
                                </span>
                              )}
                            </span>
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="flex flex-shrink-0 items-center gap-4">
            <button
              onClick={handleConfirm}
              disabled={pending}
              className="rounded bg-R300 p-3 text-lead2 font-semibold text-text-primaryHeavy disabled:opacity-60"
            >
              {pending ? 'Processing…' : 'Confirm'}
            </button>
            <button
              onClick={onClose}
              className="rounded bg-E60 p-3 text-lead2 font-semibold text-text-primaryHeavy"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};
